import { ShowBase, useShowContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { DataTable } from "@/components/admin/data-table";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Plus } from "lucide-react";

import { formatHeadcount, formatTimeRange } from "../classes/classUtils";
import type { ChampClass, Program } from "../types";

export const ProgramShow = () => (
  <ShowBase>
    <ProgramShowContent />
  </ShowBase>
);

const ProgramShowContent = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Program>();
  if (isPending || !record) return null;

  const dates = [record.starts_on, record.ends_on].filter(Boolean).join(" → ");
  const newClassUrl = `/classes/create?source=${encodeURIComponent(
    JSON.stringify({ program_id: record.id }),
  )}`;

  return (
    <div className="mt-2 flex flex-col gap-4 pb-2">
      <Card>
        <CardContent className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-60">
            <h2 className="text-2xl font-semibold">{record.name}</h2>
            <p className="text-sm text-muted-foreground">
              {[record.season, dates].filter(Boolean).join(" · ")}
            </p>
            {record.description && (
              <p className="mt-2 text-sm whitespace-pre-line">
                {record.description}
              </p>
            )}
          </div>
          <EditButton />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">
              {translate("resources.programs.classes")}
            </h3>
            <Button asChild variant="outline" size="sm">
              <Link to={newClassUrl}>
                <Plus className="h-4 w-4" />
                {translate("resources.programs.action.add_class")}
              </Link>
            </Button>
          </div>
          <ReferenceManyField
            reference="classes"
            target="program_id"
            sort={{ field: "name", order: "ASC" }}
            perPage={100}
            empty={
              <p className="text-sm text-muted-foreground">
                {translate("resources.programs.no_classes")}
              </p>
            }
          >
            <DataTable rowClick="show" bulkActionButtons={false}>
              <DataTable.Col source="name" />
              <DataTable.Col
                label="resources.classes.fields.schedule"
                disableSort
                render={(cls: ChampClass) =>
                  [
                    cls.day_of_week
                      ? translate(`resources.classes.days.${cls.day_of_week}`)
                      : "",
                    formatTimeRange(cls.start_time, cls.end_time),
                  ]
                    .filter(Boolean)
                    .join(" · ")
                }
              />
              <DataTable.Col source="location" />
              <DataTable.Col source="coach_name" />
              <DataTable.Col
                source="nb_enrolled"
                render={(cls: ChampClass) => formatHeadcount(cls)}
              />
              <DataTable.NumberCol source="nb_waitlist" />
            </DataTable>
          </ReferenceManyField>
        </CardContent>
      </Card>
    </div>
  );
};
